// import components
import { Alert, Button, LinearProgress, Typography } from '@mui/material';
import { Icon } from '@sdb/planning-ui';
import { DatePicker } from 'components/shared';
import ExcelImport, {
  ExcelImportFindings
} from 'components/shared/ExcelImport';
import HoursBalanceSettings from './components/HoursBalanceSettings';
import {
  StyledDownloadRow,
  StyledExecuteRow,
  StyledFindingsBox,
  StyledSection
} from './HoursBalanceImport.styles';

// import hooks
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useHoursBalanceImportRun,
  useHoursBalanceTemplateDownload
} from './HoursBalanceImport.hooks';

// import types
import type { ExcelFinding, ExcelRow } from 'components/shared/ExcelImport';
import type { HoursBalanceImportOptions } from 'generated';
import type { HoursBalanceExcelRow } from './HoursBalanceImport.types';

// import utils
import { startOfToday } from 'date-fns';
import {
  MAX_ROWS,
  findFileWideIssues,
  resolveHoursBalanceColumns
} from './HoursBalanceImport.utils';

const DEFAULT_OPTIONS: HoursBalanceImportOptions = {
  hoursDirection: 'TOO_MANY_WORKED',
  emptyFieldHandling: 'ZERO',
  unlistedEmployeeHandling: 'KEEP'
};

const HoursBalanceImport = () => {
  'use memo';
  const { t } = useTranslation();
  const [templateMonth, setTemplateMonth] = useState<Date | null>(
    startOfToday()
  );
  const [rows, setRows] = useState<HoursBalanceExcelRow[] | null>(null);
  const [options, setOptions] =
    useState<HoursBalanceImportOptions>(DEFAULT_OPTIONS);

  const { downloadTemplate, isReady, isPolling, hasTimedOut } =
    useHoursBalanceTemplateDownload();
  const run = useHoursBalanceImportRun();

  const onRows = (newRows: ExcelRow[] | null) => {
    setRows(newRows as unknown as HoursBalanceExcelRow[] | null);
    run.reset();
  };

  const fileWideFindings: ExcelFinding[] = rows
    ? findFileWideIssues(rows).map((issue) =>
        issue.kind === 'duplicate'
          ? {
              rowNumber: issue.rowNumber,
              expected: t(
                'adminPortal.servicedesk.hoursBalanceImport.findings.duplicateExpected'
              ),
              found: t(
                'adminPortal.servicedesk.hoursBalanceImport.findings.duplicateFound',
                { rowNumber: issue.duplicateOfRow }
              )
            }
          : {
              rowNumber: issue.rowNumber,
              expected: t(
                'adminPortal.servicedesk.hoursBalanceImport.findings.monthExpected'
              ),
              found: t(
                'adminPortal.servicedesk.hoursBalanceImport.findings.monthFound'
              )
            }
      )
    : [];

  const canExecute =
    !!rows &&
    rows.length > 0 &&
    fileWideFindings.length === 0 &&
    !run.isRunning;

  const onExecute = () => {
    if (rows) run.run(rows, options);
  };

  return (
    <div>
      <StyledSection>
        <StyledDownloadRow>
          <DatePicker
            monthYearView
            date={templateMonth}
            label={t(
              'adminPortal.servicedesk.hoursBalanceImport.downloadTemplate.month'
            )}
            onChange={setTemplateMonth}
          />
          <Button
            variant="outlined"
            startIcon={<Icon type="download" />}
            disabled={!templateMonth || !isReady || isPolling}
            onClick={() => templateMonth && downloadTemplate(templateMonth)}
          >
            {t(
              'adminPortal.servicedesk.hoursBalanceImport.downloadTemplate.button'
            )}
          </Button>
          <ExcelImport
            columns={resolveHoursBalanceColumns}
            maxRows={MAX_ROWS}
            onRows={onRows}
            disabled={run.isRunning}
          />
        </StyledDownloadRow>
        {hasTimedOut && (
          <Alert severity="warning">
            {t(
              'adminPortal.servicedesk.hoursBalanceImport.downloadTemplate.timeout'
            )}
          </Alert>
        )}
        {fileWideFindings.length > 0 && (
          <StyledFindingsBox>
            <ExcelImportFindings findings={fileWideFindings} />
          </StyledFindingsBox>
        )}
      </StyledSection>

      <StyledSection>
        <HoursBalanceSettings
          options={options}
          onChange={setOptions}
          disabled={run.isRunning}
        />
      </StyledSection>

      <StyledSection>
        <StyledExecuteRow>
          <Button
            variant="tonal"
            startIcon={<Icon type="play" />}
            disabled={!canExecute}
            onClick={onExecute}
          >
            {t('adminPortal.servicedesk.hoursBalanceImport.execute')}
          </Button>
          {run.isRunning && run.progress && (
            <LinearProgress
              variant="determinate"
              value={(run.progress.current / run.progress.total) * 100}
              // LinearProgress has no intrinsic width; as a flex item it would
              // otherwise shrink to 0px and show nothing during the whole run.
              sx={{ flexGrow: 1 }}
            />
          )}
        </StyledExecuteRow>

        {run.partialProcessed !== null && (
          <Typography>
            {t('adminPortal.servicedesk.hoursBalanceImport.summary.partial', {
              processed: run.partialProcessed
            })}
          </Typography>
        )}

        {run.summary && (
          <Typography>
            {t('adminPortal.servicedesk.hoursBalanceImport.summary.updated', {
              count: run.summary.updatedCount
            })}
            {' · '}
            {t('adminPortal.servicedesk.hoursBalanceImport.summary.skipped', {
              count: run.summary.skippedCount
            })}
            {run.summary.clearedCount != null && (
              <>
                {' · '}
                {t(
                  'adminPortal.servicedesk.hoursBalanceImport.summary.cleared',
                  {
                    count: run.summary.clearedCount
                  }
                )}
              </>
            )}
          </Typography>
        )}

        {run.findings.length > 0 && (
          <StyledFindingsBox>
            <ExcelImportFindings findings={run.findings} />
          </StyledFindingsBox>
        )}
      </StyledSection>
    </div>
  );
};

export default HoursBalanceImport;
